import { Maximize2, Plus, X } from "lucide-react";
import type { ButtonHTMLAttributes } from "react";

import sillLogo from "../../assets/sill-logo.svg";
import { cn } from "../../lib/utils";

/**
 * AdvisorDrawerHeader (plan 435 M6, task asd-drawer).
 *
 * Three controls, each a 40×40 hit area (roadmap): Expand, New conversation,
 * Close. Expand is hidden below the `md` (768px) breakpoint, where the
 * drawer renders as a full-height sheet with no "pop out to a bigger
 * surface" affordance (there IS no bigger surface on a phone-width viewport).
 */
export function AdvisorDrawerHeader({
  isStreaming,
  onClose,
  onExpand,
  onNewConversation,
}: {
  isStreaming: boolean;
  onClose: () => void;
  onExpand: () => void;
  onNewConversation: () => void;
}) {
  return (
    <div
      className="flex h-14 shrink-0 items-center justify-between border-b border-border pl-4 pr-2"
      data-slot="advisor-drawer-header"
    >
      {/* The full Ask Sill page's own "Ask [sill]" wordmark (its empty
       * state), scaled down — one brand mark for both shells (2026-10-08;
       * replaces the generic Sparkles badge). */}
      {/* gap-0.5: the logo SVG carries its own side bearing, so a wider
       * gap read as a word space and a half. */}
      <span className="flex items-center gap-0.5">
        <span className="text-[15px] font-semibold tracking-[-0.02em] text-foreground">
          Ask
        </span>
        <img
          alt="Sill"
          className="h-5 w-5 translate-y-px object-contain"
          src={sillLogo}
        />
      </span>
      <div className="flex items-center gap-0.5">
        <HeaderIconButton
          aria-label="Expand to full page"
          // `max-md:hidden` (NOT `hidden md:inline-flex`): `HeaderIconButton`'s
          // own base class already carries an unprefixed `inline-flex`, and
          // Tailwind's generated layer places `.inline-flex` AFTER `.hidden`
          // — equal specificity + later source order means the unprefixed
          // `inline-flex` always won, so this control rendered visible at
          // EVERY width (found live via Playwright at 390px; jsdom-based
          // unit tests only assert the className string, never computed
          // style, so this never surfaced before). `max-md:hidden` is itself
          // a responsive (media-scoped) utility, generated into the SAME
          // later layer section as `md:inline-flex`/`lg:inline-flex`, so it
          // reliably outranks the base `inline-flex` below the breakpoint.
          className="max-md:hidden"
          data-slot="advisor-drawer-expand"
          onClick={onExpand}
        >
          <Maximize2 aria-hidden className="h-4 w-4" />
        </HeaderIconButton>
        <HeaderIconButton
          aria-label="New conversation"
          data-slot="advisor-drawer-new-conversation"
          disabled={isStreaming}
          onClick={onNewConversation}
        >
          <Plus aria-hidden className="h-4 w-4" />
        </HeaderIconButton>
        <HeaderIconButton
          aria-label="Close"
          data-slot="advisor-drawer-close"
          onClick={onClose}
        >
          <X aria-hidden className="h-4 w-4" />
        </HeaderIconButton>
      </div>
    </div>
  );
}

function HeaderIconButton({
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color] hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      data-slot="interactive-control"
      type="button"
      {...props}
    />
  );
}
